import { tt } from '@agos/shared'

/**
 * The rules shared by every message-wording editor in this package - FRONTEND.md §9.322, BACKEND.md
 * §10A.112.
 *
 * **Why this file exists.** §9.206 built the push editor and §9.322 built the e-mail one on the same
 * payload shape, deliberately: one is `{title, body}` and the other `{subject, body}`, and every
 * *rule* between them is identical. Keeping two copies is how one of them ends up with a guard the
 * other lacks - which is the defect class FB-509 was fixing in the first place, two template
 * mechanisms where one had no reader.
 *
 * So the rules live here, generic over the name of the first field, and each editor keeps only its
 * own payload reader.
 */

/** Both editors' payloads: a first field, a body, and the defaults behind them. */
export interface TemplateDraft {
  first: string
  body: string
}

export interface TemplateRow {
  key: string
  label: string
  desc: string
  defaultFirst: string
  defaultBody: string
  /** `null` means nobody has typed one. **Not** the default - see `isOverridden`. */
  first: string | null
  body: string | null
  updatedAt: string | null
  placeholders: string[]
}

/**
 * Whether this message has been written over.
 *
 * A blank field is not an override - the server deletes the row for one - so a draft somebody
 * emptied reads as *back to the original*, which is what the button beside it promises.
 */
export function isOverridden(row: Pick<TemplateRow, 'first' | 'body'>): boolean {
  return (row.first ?? '') !== '' || (row.body ?? '') !== ''
}

/** What the recipient would actually see: what was typed, or the built-in wording. */
export function effectiveText(row: TemplateRow): TemplateDraft {
  return {
    first: (row.first ?? '').trim() === '' ? row.defaultFirst : row.first!.trim(),
    body: (row.body ?? '').trim() === '' ? row.defaultBody : row.body!.trim(),
  }
}

/**
 * The server's substitution, so the preview cannot disagree with the send.
 *
 * `SmsGateway.render` leaves an unknown placeholder standing rather than blanking it, and so does
 * this - a typed `{mano}` has to be visible as `{mano}`, not as a hole.
 */
export function fillPlaceholders(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => values[name] ?? whole)
}

/**
 * A plausible order, so the preview reads like a message rather than like a form.
 *
 * Not translated: these are a made-up customer's name and street, standing in for what the server
 * would substitute from a real order. Putting them through `tt()` would offer a translator the job
 * of renaming somebody.
 *
 * **Every placeholder either catalogue declares has a value here**, checked by a test: a preview
 * showing `{odkaz}` where the real mail carries a link teaches an owner that the link is broken.
 */
export const PREVIEW_VALUES: Record<string, string> = {
  // eslint-disable-next-line no-restricted-syntax -- sample data, not copy: a stand-in customer name
  meno: 'Jana Nováková',
  // eslint-disable-next-line no-restricted-syntax -- sample data, not copy: a stand-in address
  adresa: 'Hlavná 12, Šurany',
  cislo_objednavky: '10774',
  // §10A.428 - the points push texts' two: the points moved and the balance after.
  body: '50',
  zostatok: '150',
  // §10A.112's e-mail placeholders. `oslovenie` carries its own comma, as the server builds it.
  oslovenie: ', Jana',
  /* §9.700 - a stand-in, and only when the franchise's own name is unknown: `previewOf` puts the name the
     server substitutes here. This was *Pizza Majstro Nesvady* and a pizzamajstro.sk link, so every e-mail and
     push text Sereď edited previewed as another restaurant's - most likely the *"niekde je aj Pizza Majstro a
     neviem ti zmeniť"* of owner #12b, because it is in the preview of every text and in none of their rows. */
  // eslint-disable-next-line no-restricted-syntax -- sample data, not copy: a stand-in for the franchise's name
  restauracia: 'Vaša reštaurácia',
  odkaz: 'https://www.example.sk/reset-hesla?token=…',
  // The two below stand in for what the server substitutes, not for copy this page owns: one is a
  // driver's employment type and the other is the server's own sentence about approval. Both appear
  // only inside a preview, so translating them would put a translator's words in a preview of the
  // server's words.
  // eslint-disable-next-line no-restricted-syntax -- sample data, not copy (see above)
  typ_sofera: 'firemný šofér',
  // eslint-disable-next-line no-restricted-syntax -- sample data, not copy (see above)
  schvalenie: 'Po overení emailu sa budete môcť prihlásiť.',
}

/**
 * What the text will read like, for this franchise - §9.700.
 *
 * `restaurant` is the franchise's own name (`franchiseNameOf` the branding answer), which is what the server
 * puts in `{restauracia}`; empty falls back to the neutral stand-in. **A required parameter rather than an
 * optional one** - §9.480's rule, because a call site that drops an optional argument compiles and quietly
 * previews the stand-in where the franchise's name belongs.
 */
export function previewOf(
  row: TemplateRow,
  draft: TemplateDraft | undefined,
  restaurant: string,
): TemplateDraft {
  const source =
    draft === undefined
      ? effectiveText(row)
      : {
          first: draft.first.trim() === '' ? row.defaultFirst : draft.first,
          body: draft.body.trim() === '' ? row.defaultBody : draft.body,
        }
  const values =
    restaurant.trim() === '' ? PREVIEW_VALUES : { ...PREVIEW_VALUES, restauracia: restaurant.trim() }
  return {
    first: fillPlaceholders(source.first, values),
    body: fillPlaceholders(source.body, values),
  }
}

/**
 * Placeholders written in a draft that the server does not know.
 *
 * Reported rather than refused: `{` is a legal character in a message, and one reading *"Zľava
 * {50%}"* is somebody's wording, not a mistake. What is worth saying is that it will go out exactly
 * as written.
 */
export function unknownPlaceholders(text: string, known: readonly string[]): string[] {
  const found = text.match(/\{(\w+)\}/g) ?? []
  const names = found.map((token) => token.slice(1, -1))
  return [...new Set(names.filter((name) => !known.includes(name)))]
}

export function placeholderHint(placeholders: readonly string[]): string {
  if (placeholders.length === 0) return ''
  return `${tt('Dostupné premenné')}: ${placeholders.map((name) => `{${name}}`).join(', ')}`
}

/** Whether a draft differs from what is stored, which is what enables the save button. */
export function isDirty(row: TemplateRow, draft: TemplateDraft): boolean {
  return draft.first !== (row.first ?? '') || draft.body !== (row.body ?? '')
}

/** The draft a row opens with: what was typed, never the default - see `TemplateRow.first`. */
export function draftOf(row: TemplateRow): TemplateDraft {
  return { first: row.first ?? '', body: row.body ?? '' }
}

/**
 * A placeholder a message cannot do without.
 *
 * The password reset is the case: a body with no `{odkaz}` in it is a mail that tells a customer to
 * click a link and carries none, and the server has no way to add one back. Warned about on the
 * screen rather than refused, because a franchise may legitimately word the sentence around the
 * link differently - but never silently.
 */
export const REQUIRED_PLACEHOLDERS: Record<string, string[]> = {
  password_reset: ['odkaz'],
  staff_verification: ['odkaz'],
}

export function missingRequired(key: string, body: string): string[] {
  // A blank body is *nothing typed*, and the built-in wording has the link in it. Warning there
  // would tell every owner who has not touched this message that it is broken.
  if (body.trim() === '') return []
  return (REQUIRED_PLACEHOLDERS[key] ?? []).filter((name) => !body.includes(`{${name}}`))
}

// ─── §9.340 - a designed e-mail already sends ────────────────────────────────
//
// The owner: *"would be actually pretty great, if we use some email editor… Does our email provider
// support that?"* It does, and it already works: `EmailGateway.asHtml` passes a body beginning
// `<!DOCTYPE` or `<html` through untouched, Resend takes it as its `html` field, and §10A.124 adds
// the unsubscribe footer to that branch too. So a Templatical export sends as designed.
//
// What was missing is that the editor gave no sign of any of it - the preview escaped the markup,
// nothing said which mode a body was in, and nothing said the footer is automatic.

/**
 * Whether this body is a complete HTML document rather than paragraphs.
 *
 * **`EmailGateway.asHtml`'s rule, restated once so the two cannot disagree**: a body whose first
 * non-space characters are `<!doctype` or `<html` is passed through untouched and gets the
 * unsubscribe footer (§10A.124); anything else becomes a paragraph per blank line with `<`, `>` and
 * `&` escaped.
 *
 * **Case-insensitive on both sides since §10A.129.** The check used to be case-*sensitive* on the
 * server, so `<!doctype html>` - the HTML5 spelling, and what an e-mail builder exports - was
 * escaped and arrived as visible markup. The first draft of this file warned about that mismatch
 * from the screen; a screen apologising for a defect one line away is the wrong shape, so the rule
 * was fixed where it lives and this restates it.
 */
export function htmlBody(text: string | null | undefined): boolean {
  const start = (text ?? '').replace(/^\s+/u, '').toLowerCase()
  return start.startsWith('<!doctype') || start.startsWith('<html')
}

/** What the editor says the body will be sent as. */
export function bodyModeLabel(text: string | null | undefined): string {
  return htmlBody(text) ? tt('HTML dokument') : tt('Odstavce')
}

/**
 * The one-line rule, said on the screen rather than left in the backend.
 *
 * Somebody pasting an export needs to know what decides it, because the difference between a table
 * and the text `<table>` in a customer's inbox is one character at the start of the box.
 */
export function bodyModeNote(text: string | null | undefined): string {
  return htmlBody(text)
    ? tt('Pošle sa presne takto. Vložte sem export z e-mailového editora.')
    : tt('Pošle sa ako odstavce - prázdny riadok oddeľuje odstavec. HTML sa zobrazí ako text.')
}

/**
 * Said once under the box, because a designed template would otherwise carry its own.
 *
 * §10A.124 inserts the footer before the last `</body>` of whatever document is sent, so a template
 * with an unsubscribe block of its own ends up with two.
 */
export function footerNote(): string {
  return tt(
    'Odhlasovaciu pätičku pridáva systém automaticky - do šablóny ju nepíšte, bola by tam dvakrát.',
  )
}
